import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "roleSelect",
    "roleRadio",
    "roleCheckbox",
    "roleCheckboxContainer",
    "organizationSection",
    "brandSection",
    "channelSection",
    "campaignSection",
    "permissionsWrapper",
  ]

  static values = {
    brandManagerRoleId: String,
    brandOwnerRoleId: String,
    editorRoleId: String,
    permissions: Object
  }

  connect() {
    // this.updateVisibility()
  }

  onChange() {
    // this.updateVisibility()
  }

  onRoleChange(event) {
    // Update label styles (works for both radio and checkbox)
    this.updateRoleLabelStyles(event.target)

    const selectedRoleIds = this.selectedRoleIds()

    if (selectedRoleIds.length === 0) {
      this.permissionsWrapperTarget.innerHTML = ""
      return
    }

    const currentStates = this.preserveCheckboxStates()

    fetch("/users/role_permissions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector("meta[name='csrf-token']").content
      },
      body: JSON.stringify({ role_ids: selectedRoleIds })
    })
      .then(r => r.text())
      .then(html => {
        this.permissionsWrapperTarget.innerHTML = html
        this.restoreCheckboxStates(currentStates)
      })
  }

  updateRoleLabelStyles(changedInput) {
    // Support checkboxes (multiple roles)
    if (this.hasRoleCheckboxTarget) {
      this.roleCheckboxTargets.forEach(cb => {
        const label = cb.closest('label')
        if (!label) return
        const checkSpan = label.querySelector('span:last-child')
        const isChecked = cb.checked
        if (isChecked) {
          label.classList.remove('border-transparent')
          label.classList.add('border-toggle')
          if (checkSpan) {
            checkSpan.classList.remove('border-[#96A7C7]')
            checkSpan.classList.add('border-toggle')
            if (!checkSpan.querySelector('svg')) {
              const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
              svg.setAttribute('class', 'w-3 h-3 text-toggle')
              svg.setAttribute('fill', 'none')
              svg.setAttribute('stroke', 'currentColor')
              svg.setAttribute('stroke-width', '3')
              svg.setAttribute('viewBox', '0 0 24 24')
              svg.innerHTML = '<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>'
              checkSpan.appendChild(svg)
            }
          }
        } else {
          label.classList.remove('border-toggle')
          label.classList.add('border-transparent')
          if (checkSpan) {
            checkSpan.classList.remove('border-toggle')
            checkSpan.classList.add('border-[#96A7C7]')
            const svg = checkSpan.querySelector('svg')
            if (svg) svg.remove()
          }
        }
      })
      return
    }
    // Legacy: radio buttons
    this.roleRadioTargets.forEach(r => {
      const label = r.closest('label') || document.querySelector(`label[for="${r.id}"]`)
      if (!label) return
      const radioDotSpan = label.querySelector('span:last-child')
      const isChecked = r.checked
      if (isChecked) {
        label.classList.remove('border-transparent')
        label.classList.add('border-toggle')
      } else {
        label.classList.remove('border-toggle')
        label.classList.add('border-transparent')
      }
      if (radioDotSpan) {
        if (isChecked) {
          radioDotSpan.classList.remove('border-[#96A7C7]')
          radioDotSpan.classList.add('border-toggle')
          if (!radioDotSpan.querySelector('span')) {
            const dot = document.createElement('span')
            dot.className = 'w-2 h-2 rounded-full bg-toggle'
            radioDotSpan.appendChild(dot)
          }
        } else {
          radioDotSpan.classList.remove('border-toggle')
          radioDotSpan.classList.add('border-[#96A7C7]')
          const dot = radioDotSpan.querySelector('span')
          if (dot) dot.remove()
        }
      }
    })
  }

  preserveCheckboxStates() {
    const states = {}
    // Get all permission checkboxes
    const permissionCheckboxes = this.permissionsWrapperTarget.querySelectorAll(
      'input[type="checkbox"][name*="[permissions]"]'
    )
    permissionCheckboxes.forEach(cb => {
      const key = `${cb.name}|${cb.value}`
      states[key] = cb.checked
    })

    // Get all channel checkboxes
    const channelCheckboxes = this.permissionsWrapperTarget.querySelectorAll(
      'input[type="checkbox"][name="channel_ids[]"]'
    )
    channelCheckboxes.forEach(cb => {
      const key = `channel_ids[]|${cb.value}`
      states[key] = cb.checked
    })

    return states
  }

  restoreCheckboxStates(states) {
    // Restore permission checkboxes
    const permissionCheckboxes = this.permissionsWrapperTarget.querySelectorAll(
      'input[type="checkbox"][name*="[permissions]"]'
    )
    permissionCheckboxes.forEach(cb => {
      const key = `${cb.name}|${cb.value}`
      if (states.hasOwnProperty(key)) {
        cb.checked = states[key]
        // Trigger change event to update visual state
        cb.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    // Restore channel checkboxes
    const channelCheckboxes = this.permissionsWrapperTarget.querySelectorAll(
      'input[type="checkbox"][name="channel_ids[]"]'
    )
    channelCheckboxes.forEach(cb => {
      const key = `channel_ids[]|${cb.value}`
      if (states.hasOwnProperty(key)) {
        cb.checked = states[key]
        cb.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }

  updateVisibility() {
    const selectedRoleIds = this.selectedRoleIds()

    const isBrandManager   = selectedRoleIds.includes(this.brandManagerRoleIdValue)
    const isBrandOwner     = selectedRoleIds.includes(this.brandOwnerRoleIdValue)
    const isEditor         = selectedRoleIds.includes(this.editorRoleIdValue)

    this.toggle(
      this.organizationSectionTarget,
      !(isBrandManager || isBrandOwner || isEditor)
    )

    this.toggle(this.brandSectionTarget, isBrandManager || isBrandOwner)
    this.toggle(this.channelSectionTarget, isEditor)
    this.toggle(this.campaignSectionTarget, false) // Campaign section not used anymore
  }

  selectedRoleIds() {
    // Multiple roles (checkboxes)
    if (this.hasRoleCheckboxTarget) {
      return this.roleCheckboxTargets
        .filter(cb => cb.checked)
        .map(cb => cb.value)
    }

    if (this.hasRoleRadioTarget) {
      const selected = this.roleRadioTargets.find(r => r.checked)
      return selected ? [selected.value] : []
    }

    if (this.hasRoleSelectTarget) {
      return Array.from(this.roleSelectTarget.selectedOptions)
        .map(option => option.value)
    }

    return []
  }

  toggle(element, show) {
    if (!element) return
    element.style.display = show ? "block" : "none"
  }
}
